import React, { useState, useEffect, useCallback } from "react";
import Loader from "../loader/Loader";
import { Button, Switch, Box } from "@mui/material";
import MUIDataTable from "mui-datatables";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import SentimentSatisfied from "@mui/icons-material/SentimentSatisfied";
import SentimentDissatisfied from "@mui/icons-material/SentimentDissatisfied";
import { grey, green, red } from "@mui/material/colors";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import axios from "axios";
import AddWeight from "./AddWeight";
import {
  hideConfirmModal,
  showConfirmModal,
  showModal,
  storeWeightList,
} from "../../redux/counterSlice";
import {
  useTablePagination,
  useTableHeight,
} from "../../hooks/useTablePagination";
import { usePermission } from "../../hooks/usePermission";

const Weight = () => {
  const dispatch = useDispatch();
  const { reset } = useForm();
  const permissions = usePermission("weight");

  // const screen = "weight";
  const {
    page,
    rowsPerPage,
    searchText,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
  } = useTablePagination("weight", 10);
  const tableHeight = useTableHeight();

  const weightList = useSelector((state) => state.counter.weightList || []);
  const [loader, setLoader] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeWeightList, setActiveWeightList] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const hasActionPermission = permissions?.edit || permissions?.delete;

  const loadInitialData = useCallback(async () => {
    try {
      setLoader(true);
      const response = await axios.get("/getAllWeightList");
      dispatch(storeWeightList(response.data?.data || []));
    } catch (err) {
      toast.error(err?.message || "Failed to fetch weight list");
    } finally {
      setLoader(false);
      dispatch(hideConfirmModal({ msg: "" }));
    }
  }, [dispatch, page, rowsPerPage]);

  const loadActiveWeights = async () => {
    try {
      const response = await axios.get("/getAllActiveWeightList");
      setActiveWeightList(response.data?.data || []);
    } catch (err) {
      toast.error(err?.message || "Failed to fetch active weight list");
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const toggleStatus = (weight_id, currentStatus) => {
    const endpoint =
      currentStatus === 1
        ? `/deActivateWeight/${weight_id}`
        : `/activateWeight/${weight_id}`;
    setLoader(true);
    axios
      .get(endpoint)
      .then(() => {
        toast.success(
          currentStatus === 1 ? "Weight deactivated" : "Weight activated"
        );
        loadInitialData();
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to toggle status");
      })
      .finally(() => {
        setLoader(false);
        dispatch(hideConfirmModal({ msg: "" }));
      });
  };

  const deleteClickHandler = async (id) => {
    try {
      const response = await axios.get(`/deleteWeight/${id}`);
      if (response.data?.error) {
        let errDesc = response.data?.message || "Something went wrong";
        if (response.data?.data) {
          errDesc = Object.values(response.data.data).join("\n");
        }
        dispatch(showModal({ msg: errDesc, error: true }));
      } else {
        toast.success(response.data?.message || "Deleted successfully");
        loadInitialData();
        handleClose();
      }
    } catch (error) {
      dispatch(
        showModal({
          msg:
            error?.response?.data?.message || error?.message || "Server Error",
          error: true,
        })
      );
    } finally {
      dispatch(hideConfirmModal({ msg: "" }));
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
    reset();
  };

  const editClickHandler = (item) => {
    loadActiveWeights();
    setSelectedRow({ ...item });
    setOpen(true);
  };

  // const filteredWeightList = weightList.filter((weight) =>
  //   weight.weight_name.toLowerCase().includes(searchText.toLowerCase())
  // );

  const filteredWeightList = weightList
    .filter((weight) =>
      weight.weight_name.toLowerCase().includes(searchText.toLowerCase())
    )
    .sort((a, b) => {
      const unitA = a.unit?.unit_name?.toLowerCase() || "";
      const unitB = b.unit?.unit_name?.toLowerCase() || "";
      if (unitA < unitB) return -1;
      if (unitA > unitB) return 1;
      return 0;
    });

  const columns = [
    {
      name: "id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => <p>{+dataIndex + 1}</p>,
        filter: false,
      },
    },
    {
      name: "weight_name",
      label: "Weight Name",
      options: {
        customBodyRenderLite: (dataIndex) =>
          filteredWeightList[dataIndex]?.weight_name || "-",
      },
    },
    {
      name: "unit_name",
      label: "Unit Name",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) =>
          filteredWeightList[dataIndex]?.unit?.unit_name || "-",
      },
    },
    {
      name: "allow_cod",
      label: "Allow Cod",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const allow = filteredWeightList[dataIndex]?.allow_cod;
          return allow === 1 ? (
            <Box sx={{ color: green[600], alignItems: "center" }}>
              <SentimentSatisfied fontSize="small" sx={{ mr: 1 }} />
              Yes
            </Box>
          ) : (
            <Box sx={{ color: red[600], alignItems: "center" }}>
              <SentimentDissatisfied fontSize="small" sx={{ mr: 1 }} />
              No
            </Box>
          );
        },
      },
    },
    {
      name: "active_status",
      label: "Status",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const item = filteredWeightList[dataIndex];
          const status = item?.active_status;

          return (
            <Switch
              size="small"
              color={status === 1 ? "success" : "error"}
              checked={status === 1}
              onChange={() =>
                dispatch(
                  showConfirmModal({
                    msg: status === 1 ? "Deactivate" : "Activate",
                    action: () => toggleStatus(item.weight_id, status),
                  })
                )
              }
              style={{ marginBottom: "5px" }}
            />
          );
        },
        filter: true,
        filterOptions: {
          names: ["Active", "Inactive"],
          logic(value, filters) {
            if (filters.length) {
              if (filters.includes("Active") && +value === 1) return false;
              if (filters.includes("Inactive") && +value === 0) return false;
              return true;
            }
            return false;
          },
        },
      },
    },
    ...(hasActionPermission
      ? [
          {
            name: "Actions",
            label: "Actions",
            options: {
              filter: false,
              customBodyRenderLite: (dataIndex) => {
                const item = filteredWeightList[dataIndex];
                const status = item?.active_status;

                return (
                  <div className="actionBtns" style={{ gap: "8px" }}>
                    {permissions?.edit && (
                      <Button
                        disabled={status === 1}
                        aria-label="edit"
                        onClick={() => editClickHandler(item)}
                      >
                        <EditIcon
                          fontSize="medium"
                          sx={{ color: status === 1 ? grey[600] : "secondary" }}
                        />
                      </Button>
                    )}
                    {permissions?.delete && (
                      <Button
                        disabled={status === 1}
                        aria-label="delete"
                        onClick={() =>
                          dispatch(
                            showConfirmModal({
                              msg: "Delete",
                              action: () => deleteClickHandler(item.weight_id),
                            })
                          )
                        }
                      >
                        <DeleteOutlineOutlinedIcon
                          sx={{ color: status === 1 ? grey[600] : "red" }}
                          fontSize="medium"
                        />
                      </Button>
                    )}
                  </div>
                );
              },
            },
          },
        ]
      : []),
  ];

  const options = {
    selectableRows: "none",
    filter: true,
    download: false,
    viewColumns: true,
    print: false,
    rowsPerPage: rowsPerPage,
    page: page,
    fixedHeader: true,
    tableBodyHeight: tableHeight,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
    searchText: searchText,
    textLabels: {
      body: {
        noMatch: loader ? "Loading..." : "No data available",
      },
    },
  };

  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <h4 className="text-dark">Weight</h4>
          <MUIDataTable
            data={filteredWeightList}
            columns={columns}
            options={options}
            title={
              permissions.add ? (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    loadActiveWeights();
                    setSelectedRow(null);
                    setOpen(true);
                  }}
                  startIcon={<AddIcon />}
                >
                  New Weight
                </Button>
              ) : null
            }
          />
        </>
      )}

      {activeWeightList?.length > 0 && (
        <AddWeight
          // data={activeWeightList}
          open={open}
          handleClose={handleClose}
          selectedRow={selectedRow}
          loadInitialData={loadInitialData}
        />
      )}
    </>
  );
};

export default Weight;
